import { QueryClient } from '@tanstack/react-query';

/**
 * Shared React Query client.
 *
 * Defaults are tuned for users on slow/unreliable connections: once data is
 * fetched it is treated as fresh for several minutes and kept in memory for a
 * while after a screen unmounts, so navigating away and back is instant with
 * NO network round-trip.
 *
 * Liveness does NOT come from short polling here — it comes from the WebSocket
 * invalidation channel (see RealtimeQuerySync). When the server says an entity
 * changed, the matching query is invalidated and refetched immediately, so the
 * long staleTime never causes another user to see outdated data.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Within this window a remount serves from cache with no refetch.
      staleTime: 5 * 60 * 1000, // 5 minutes
      // How long an unused query stays cached before garbage collection.
      // Long enough to align with the sessionStorage persistence (see
      // queryPersister) so refreshed/rehydrated queries aren't dropped early.
      cacheTime: 24 * 60 * 60 * 1000, // 24 hours
      // Don't refetch just because the tab regained focus — costly on weak links
      // and unnecessary since the WebSocket already pushes real changes.
      refetchOnWindowFocus: false,
      // Refetching after a dropped connection comes back is cheap and useful.
      refetchOnReconnect: true,
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
    mutations: {
      retry: 0,
    },
  },
});
